import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import type { GetEnvironmentDataResponse, User } from '../ai.h2o.cloud.appstore';
import { AuthService, EnvService } from './api';

export const cacheKeys = {
  cookieSession: (jwtToken: string) => [`appstore`, `auth`, `cookieSession`, jwtToken],
  environmentData: [`appstore`, `env`, `data`],
  user: [`appstore`, `user`],
};

export const useQueryEnvironmentData = <TData = GetEnvironmentDataResponse>(
  options?: Omit<UseQueryOptions<GetEnvironmentDataResponse, unknown, TData>, `queryFn` | `queryKey`>
) => {
  const baseOptions: UseQueryOptions<GetEnvironmentDataResponse, unknown, TData> = {
    queryFn: () => EnvService.getEnvironmentData({}),
    queryKey: cacheKeys.environmentData,
    staleTime: 5 * 60 * 1000, // 5 minutes
  };
  return useQuery({
    ...options,
    ...baseOptions,
  });
};

export const useQueryAppstoreUser = <TData = User>(
  options?: Omit<UseQueryOptions<User, unknown, TData>, `queryFn` | `queryKey`>
) => {
  const baseOptions: UseQueryOptions<User, unknown, TData> = {
    queryFn: async () => {
      const response = await AuthService.checkAuth({});
      return response.user;
    },
    queryKey: cacheKeys.user,
    staleTime: 5 * 60 * 1000, // 5 minutes
  };

  return useQuery({
    ...options,
    ...baseOptions,
  });
};

/**
 * Set cookie for Wave app authentication
 */
export const useQueryCookieSession = (jwtToken: string | undefined) => {
  return useQuery({
    cacheTime: Number.POSITIVE_INFINITY,
    enabled: Boolean(jwtToken),
    queryFn: async ({ signal }) => {
      const envURL = process.env.REACT_APP_API_PATH || '';
      const sessionUrl = `${envURL}/auth/session`;

      const response = await fetch(sessionUrl, {
        method: 'GET',
        signal,
        headers: {
          Authorization: `Bearer ${jwtToken}`,
        },
      });

      if (!response.ok) {
        if (response.headers.get('Content-Type')?.includes('application/json')) {
          throw await response.json();
        }
        throw new Error(`Failed to fetch cookie session: ${await response.text()}`);
      }
      return true;
    },
    queryKey: cacheKeys.cookieSession(jwtToken || ``),
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Number.POSITIVE_INFINITY,
  });
};
